/**
 * imageStorage.js — localStorage-based image storage for procurement items
 *
 * Storage key:  proc_img_{itemKey}
 * Value shape:  { images: [{ id, name, thumb, small, large, url, timestamp }], updated: ts }
 *
 * Image sizes generated on save:
 *   thumb  = 64×64  JPEG q0.6  (grid thumbnail)
 *   small  = 300×300 JPEG q0.75 (inspector preview)
 *   large  = 800px max JPEG q0.88 (full view / print)
 *
 * Returns image reference for DB:  { imageKey, count, hasImage: true }
 */

const PREFIX = 'proc_img_';

/** Resize a base64 image to maxW×maxH, returns new base64 */
export function resizeImage(dataUrl, maxW, maxH, quality = 0.8) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      let w = img.naturalWidth, h = img.naturalHeight;
      const ratio = Math.min(maxW / w, maxH / h, 1);
      w = Math.round(w * ratio);
      h = Math.round(h * ratio);
      const canvas = document.createElement('canvas');
      canvas.width  = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Crop a base64 image using a rect {x,y,w,h} in pixels of the displayed canvas */
export function cropImage(dataUrl, srcRect, displaySize) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const scaleX = img.naturalWidth  / displaySize.w;
      const scaleY = img.naturalHeight / displaySize.h;
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(srcRect.w * scaleX);
      canvas.height = Math.round(srcRect.h * scaleY);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img,
        srcRect.x * scaleX, srcRect.y * scaleY,
        srcRect.w * scaleX, srcRect.h * scaleY,
        0, 0, canvas.width, canvas.height
      );
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Rotate a base64 image by deg (90, 180, 270) */
export function rotateImage(dataUrl, deg) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const rad = deg * Math.PI / 180;
      if (deg === 90 || deg === 270) {
        canvas.width  = img.naturalHeight;
        canvas.height = img.naturalWidth;
      } else {
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
      }
      const ctx = canvas.getContext('2d');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(rad);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Generate all 3 sizes from a source base64 */
export async function generateSizes(dataUrl) {
  const [thumb, small, large] = await Promise.all([
    resizeImage(dataUrl, 64,  64,  0.6),
    resizeImage(dataUrl, 300, 300, 0.75),
    resizeImage(dataUrl, 800, 800, 0.88),
  ]);
  return { thumb, small, large };
}

/** Load images for an item key */
export function loadImages(itemKey) {
  try {
    const raw = localStorage.getItem(PREFIX + itemKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return parsed.images || [];
  } catch {
    return [];
  }
}

/** Save images array for an item key — returns DB reference object */
export function saveImages(itemKey, images) {
  const data = { images, updated: Date.now() };
  try {
    localStorage.setItem(PREFIX + itemKey, JSON.stringify(data));
  } catch (e) {
    // localStorage quota exceeded — remove oldest
    if (e.name === 'QuotaExceededError') {
      console.warn('[imageStorage] Quota exceeded, clearing old images');
      clearOldImages();
      try { localStorage.setItem(PREFIX + itemKey, JSON.stringify(data)); } catch {}
    }
  }
  // Return DB reference object (to be saved alongside item data)
  return {
    imageKey:  PREFIX + itemKey,
    imageCount: images.length,
    hasImage:  images.length > 0,
    firstThumb: images[0]?.thumb || null,
  };
}

/** Delete images for an item key */
export function deleteImages(itemKey) {
  localStorage.removeItem(PREFIX + itemKey);
}

/** Remove the oldest proc_img_ entries to free space */
function clearOldImages() {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(PREFIX)) keys.push(k);
  }
  // Remove the first half (oldest)
  keys.slice(0, Math.max(1, Math.floor(keys.length / 2))).forEach(k => localStorage.removeItem(k));
}
